/**
 * Payment state readers. The one place the payment screen's slice keys are named.
 *
 * Each returns a PRIMITIVE or a store-owned reference, never a fresh object —
 * `useSelector` compares by identity, and a selector that builds the tile list
 * would re-render the payment screen on every action the store sees. Building
 * them is `paymentModel`'s job and the hook memoises it.
 */
import { selectEngine } from "@rista/payments/engine/session";
import { isLegInFlight } from "@rista/payments/engine/machine";

import { selectKioskPaymentModeIds } from "../config/selectors";
import type { KioskDevice, KioskDevicePayment } from "./deviceTypes";

/**
 * The shared empty. `useSelector` compares by IDENTITY, so a selector that ends in `?? []`
 * hands back a brand-new array on every call for the very common case of "not configured
 * yet" — and every store notification then looks like a change. Downstream that is not a
 * wasted render but an infinite loop: the payment hook derives its record from these, the
 * engine re-seeds on a new record, the re-seed dispatches, and the dispatch re-runs the
 * selector. One frozen constant ends it.
 */
const NONE: any[] = [];

/** The merchant's payment definitions, exactly as `businessData` carries them. */
export function selectPaymentDefs(state: any): any[] {
  return state?.businessStore?.businessData?.paymentdefs ?? NONE;
}

/**
 * The channel the sale is on. Off the SALE first, as `getPermittedPaymentModes`
 * does — the sale's own channel is authoritative once one has been created, and
 * a def that names channels is filtered against exactly this.
 */
export function selectSaleChannelDefId(state: any): string {
  return (
    state?.saleDataStore?.saleData?.channelDefId ??
    state?.saleDataStore?.selectedChannelData?.channelDefId ??
    ""
  );
}

export function selectIsOffline(state: any): boolean {
  return Boolean(state?.businessStore?.isOffline);
}

/**
 * The number the customer is called by on the success screen — the placed sale's
 * `orderNumber`, copied onto the session by `markOrderPlaced`.
 *
 * Read from the SESSION rather than through to `saleDataStore`, because the success screen
 * outlives the sale it describes: its normal end is a 20s pause and then `resetSession`,
 * which clears the sale as a privacy control. See `KioskState.orderToken`.
 *
 * ⚠️ It is DISPLAY ONLY. It used to be what made SUCCESS a legal route, and that was the
 * bug: see `selectSubmittedSaleMid` below, which the guard reads now. A sale can be placed
 * and carry no number at all.
 */
export function selectOrderToken(state: any): string | null {
  return state?.kioskStore?.orderToken ?? null;
}

/**
 * The placed sale's `mid` — the proof that an order EXISTS, as opposed to the number a
 * customer is called by.
 *
 * 🔴 This is what makes SUCCESS a legal route (`selectHasPlacedOrder` in
 * `kiosk/navigation/selectors`), and it is deliberately not `orderToken`.
 *
 * `mid` is the sale's primary key: the client mints it in `getBasicSaleData` and the server
 * echoes it back, so every placed sale has one. `orderNumber` is OPTIONAL on the model
 * (`orderNumber?: string`, beside `billNumber` and `offlineNumber`) and whether the backend
 * stamps one on a still-OPEN save — which is exactly what "pay at counter" writes — is
 * per-merchant configuration. Gating the route on it meant a customer at one branch placed
 * their order and was then bounced to the attract screen by the guard, because the thing
 * being asked for was a DISPLAY value rather than evidence of the write.
 */
export function selectSubmittedSaleMid(state: any): string | null {
  return state?.kioskStore?.submittedSaleMid ?? null;
}

/** The customer chose to pay at the counter, so the success copy says so. */
export function selectPayAtCounter(state: any): boolean {
  return Boolean(state?.kioskStore?.payAtCounter);
}

/**
 * The bill link to encode as the "Scan for e-receipt" code — the placed sale's `url`
 * ("Sale URL, used for QR code printing", `models/sale`), the same link the POS shows
 * after a sale (`PostSalePopup:45`). Copied onto the session for the reason above.
 *
 * `null` for a sale the backend gave no URL, and the success screen then draws no code
 * rather than a decorative one that scans to nothing.
 */
export function selectReceiptUrl(state: any): string | null {
  return state?.kioskStore?.receiptUrl ?? null;
}

export { selectKioskPaymentModeIds };

/* ── the DEVICE record (`kioskdevice/fetch`) ──────────────────────────────────────────── */

/**
 * ⚠️ A FROZEN shared empty, not `?? []`. `useSelector` compares by identity, so a fresh array
 * per call makes every store notification look like a change — and downstream of the payment
 * hook that is not a wasted render but a loop: the hook derives its record from these, the
 * engine re-seeds on a new record, and the re-seed dispatches. Same reasoning, and the same
 * constant, as `NONE` at the top of this file.
 */
const NO_PAYMENTS: readonly KioskDevicePayment[] = Object.freeze([]);

/** This panel's record, or `null` before the first successful fetch. */
export function selectKioskDevice(state: any): KioskDevice | null {
  return state?.kioskConfigStore?.device ?? null;
}

/**
 * Is THIS panel paired for payment? `kioskdevice/fetch` answered a record (or one is persisted
 * from before). The cart's "Proceed to payment" and the menu's "Start Kiosk" both wait on it.
 */
export function selectHasKioskDevice(state: any): boolean {
  return selectKioskDevice(state) != null;
}

/** What this panel can take — the list `buildKioskPaymentMethods` draws its tiles from. */
export function selectKioskDevicePayments(state: any): readonly KioskDevicePayment[] {
  return state?.kioskConfigStore?.device?.payment ?? NO_PAYMENTS;
}

export function selectIsFetchingKioskDevice(state: any): boolean {
  return !!state?.kioskConfigStore?.isFetchingDevice;
}

export function selectKioskDeviceError(state: any): string | null {
  return state?.kioskConfigStore?.deviceError ?? null;
}

/**
 * Is money moving: a payment with its provider, or the order's save on its way to the server?
 *
 * What holds the customer on the payment screen (`KioskApp` hands it to `RouteGuard`, whose
 * `isRouteLocked` sends any other route back to PAYMENT). The kiosk monkey found the save half
 * (seed 158): Pay at counter, then Back while the save was out, then Pay with UPI on the same
 * sale, a UPI payment started beside an unanswered counter order. A boolean, so `useSelector`
 * re-renders only when it flips.
 *
 * Wider than `isKioskPaymentInFlight` on purpose: that one also pauses the idle timer, and a
 * save does not need the timer paused (it answers in seconds; the payment route allows 180 s).
 */
export function selectIsKioskPaymentBusy(state: any): boolean {
  // `KioskApp` reads this on every screen; a store with no engine slice has no money moving, and
  // answering that beats throwing on the attract screen (the same contract as the mode guard)
  const engine = selectEngine(state);
  if (!engine) return false;
  return engine.committing || engine.legs.some((leg) => isLegInFlight(leg.legState));
}
